import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ApiError, type Address, type QuoteRow } from '@chicare/shared';

import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import { Icon } from '@/components/icons/Icon';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';

/**
 * Where is it going, what does that cost, and then pay.
 *
 * ── THE ONE RULE THIS SCREEN EXISTS TO KEEP ─────────────────────────────────
 *
 * Never show somebody a total, take their acceptance, and then charge more.
 * Delivery is priced by zone and by weight, both of which are known before
 * anybody commits — so the buyer picks the address, watches the figure move,
 * and presses a button that says the number they will actually be charged.
 *
 * ── AND WHY IT EXPLAINS ITSELF ──────────────────────────────────────────────
 *
 * "Why is delivery ₦3,000?" is the question this step exists to answer before
 * it is asked. So it names the zone, the weight band and the reason — free over
 * a threshold, or a different state, or simply a heavier parcel. A fee nobody
 * can explain is a fee somebody queries, and a queried fee costs more in
 * support than it ever earned.
 */
export function AcceptQuoteSheet({
  requestReference,
  quote,
  onAccept,
  onCancel,
  busy,
  error,
}: {
  requestReference: string;
  quote: QuoteRow;
  onAccept: (input: { addressId?: number; method: string }) => void;
  onCancel: () => void;
  busy: boolean;
  error: ApiError | null;
}) {
  const [addressId, setAddressId] = useState<number | undefined>(undefined);
  const [method, setMethod] = useState('standard');

  const addresses = useQuery({
    queryKey: ['addresses'],
    queryFn: ({ signal }) => api.addresses(signal),
  });

  const delivery = useQuery({
    queryKey: ['quote-delivery', requestReference, quote.reference, addressId],
    queryFn: ({ signal }) => api.quoteDelivery(requestReference, quote.reference, addressId, signal),
  });

  const chosen = delivery.data?.options.find((option) => option.method === method);
  const meta = delivery.data?.meta;

  return (
    <div className="border-t border-line bg-paper px-4 py-4">
      <h3 className="m-0 font-display text-[16px] font-bold tracking-[-0.015em]">
        Where should it go?
      </h3>

      {/* ── The address ───────────────────────────────────────────────────
          Listed rather than assumed. A buyer may be somewhere other than the
          address they asked from — which is exactly why they were allowed to
          pick a pharmacy in another state. */}
      {addresses.isLoading ? (
        <Skeleton className="mt-3 h-16 w-full" radius="card" />
      ) : (addresses.data?.length ?? 0) === 0 ? (
        <p className="m-0 mt-2 text-[13px] leading-relaxed text-muted">
          You have no saved address yet. You will be asked for one at checkout, and the delivery fee
          is worked out then.
        </p>
      ) : (
        <ul className="m-0 mt-2.5 list-none space-y-1.5 p-0">
          {(addresses.data ?? []).map((address) => (
            <AddressChoice
              key={address.id}
              address={address}
              selected={
                addressId === address.id || (addressId === undefined && Boolean(address.is_default))
              }
              onSelect={() => setAddressId(address.id)}
            />
          ))}
        </ul>
      )}

      {/* ── The options, priced for that address ──────────────────────────── */}
      <h3 className="m-0 mt-4 font-display text-[16px] font-bold tracking-[-0.015em]">
        How should it come?
      </h3>

      {delivery.isLoading ? (
        <Skeleton className="mt-2.5 h-24 w-full" radius="card" />
      ) : (
        <ul className="m-0 mt-2.5 list-none space-y-1.5 p-0">
          {(delivery.data?.options ?? []).map((option) => (
            <li key={option.method}>
              <button
                type="button"
                role="radio"
                aria-checked={method === option.method}
                onClick={() => setMethod(option.method)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-card border p-3 text-left',
                  method === option.method
                    ? 'border-blue bg-blue-tint-2'
                    : 'border-line bg-white hover:border-faint',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-2',
                    method === option.method
                      ? 'border-[5.5px] border-blue bg-white'
                      : 'border-line bg-white',
                  )}
                />
                <span className="min-w-0 flex-1">
                  <span className="block text-[13.5px] font-semibold text-ink">{option.label}</span>
                  <span className="block text-[11.5px] text-faint">{option.eta}</span>
                </span>
                <span className="font-mono text-[13px] font-bold text-ink tabular">
                  {option.fee_kobo === 0 ? 'Free' : option.fee_display}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* ── Why it costs what it costs ────────────────────────────────────── */}
      {meta ? (
        <p className="m-0 mt-3 border-t border-line pt-2.5 text-[11.5px] leading-relaxed text-muted">
          {meta.is_free ? (
            <>Delivery is free on orders over {meta.free_over_display}.</>
          ) : meta.crosses_state ? (
            <>
              {quote.pharmacy} is in another state from this address, so this is an interstate
              delivery rather than a local one.
            </>
          ) : (
            <>
              {meta.zone ? <>Priced for {meta.zone}</> : <>Priced at the standard rate</>}
              {meta.band ? <> · {meta.band}</> : null}
              {meta.weight_grams > 0 ? (
                <> · about {formatWeight(meta.weight_grams)} in the bag</>
              ) : null}
              {meta.free_over_display ? <> · free over {meta.free_over_display}</> : null}
            </>
          )}
        </p>
      ) : null}

      {/* ── The total, which is the number they will be charged ─────────────
          Deliberately blank until delivery has resolved. Showing the goods
          figure while the fee is still loading would put a number on screen
          that nobody will ever be charged — which is the exact thing this whole
          step exists to prevent, and being disabled is not an excuse for it. */}
      <div className="mt-3 flex items-baseline justify-between gap-3 border-t border-line pt-3">
        <span className="text-[12.5px] text-muted">
          {chosen
            ? `${quote.goods_total_display} + ${chosen.fee_kobo === 0 ? 'free' : chosen.fee_display} delivery`
            : 'Working out delivery…'}
        </span>
        <span className="font-display text-[21px] font-bold tracking-[-0.02em] text-blue tabular">
          {chosen?.all_in_display ?? '—'}
        </span>
      </div>

      {error ? (
        <p role="alert" className="m-0 mt-3 text-[13px] font-medium leading-relaxed text-danger">
          {error.message}
        </p>
      ) : null}

      <Button
        className="mt-3 w-full"
        disabled={busy || !chosen}
        onClick={() => onAccept({ addressId, method })}
      >
        {busy
          ? 'Accepting…'
          : chosen
            ? `Accept ${chosen.all_in_display} and pay`
            : 'Working out delivery…'}
      </Button>

      <button
        type="button"
        onClick={onCancel}
        className="mt-2 min-h-11 w-full bg-transparent text-[12.5px] font-bold text-muted"
      >
        Not this one
      </button>

      {/* Said "you check the pack before the medicines are charged", which is
          the door-capture case only — and a prescription is POM, which §9.18
          sends to CHECKOUT capture every time. The check at the door is real;
          what is not real is that it comes before the money. The protection is
          the refund (§4.6), not the order of events, so say that instead. */}
      <p className="m-0 mt-1 text-[11.5px] leading-relaxed text-muted">
        Delivery is charged when you order. Check the pack when it arrives — anything wrong and
        you are refunded.
      </p>
    </div>
  );
}

function AddressChoice({
  address,
  selected,
  onSelect,
}: {
  address: Address;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={onSelect}
        className={cn(
          'flex w-full items-start gap-2.5 rounded-card border p-3 text-left',
          selected ? 'border-blue bg-blue-tint-2' : 'border-line bg-white hover:border-faint',
        )}
      >
        <span
          aria-hidden
          className={cn(
            'mt-0.5 grid h-[18px] w-[18px] shrink-0 place-items-center rounded-full border-2',
            selected ? 'border-[5.5px] border-blue bg-white' : 'border-line bg-white',
          )}
        />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="text-[13.5px] font-semibold text-ink">{address.label}</span>
            {address.is_default ? (
              <span className="font-mono text-[9px] uppercase tracking-[0.1em] text-faint">
                Default
              </span>
            ) : null}
          </span>
          <span className="mt-0.5 block text-[11.5px] leading-snug text-muted">
            {[address.line1, address.lga, address.state].filter(Boolean).join(', ')}
          </span>
        </span>
        <Icon name="pin" size={14} strokeWidth={1.9} className="mt-0.5 shrink-0 text-faint" />
      </button>
    </li>
  );
}

/** Grams in the data, something a person can picture on the screen. */
function formatWeight(grams: number): string {
  return grams < 1000 ? `${grams}g` : `${(grams / 1000).toFixed(1).replace(/\.0$/, '')}kg`;
}
